import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  AvatarMedium,
  PaginationLinks,
  NamedLink,
  ResponsiveImage,
  ToolLogo,
} from '../../components';

import { ensureUser } from '../../util/data';
import { trackIntercomEvent } from '../../util/intercom';
import { FaStar, FaHeart, FaRegHeart } from 'react-icons/fa';
import { BiSolidQuoteAltRight } from 'react-icons/bi';

import postJobBannerCheck from '../../assets/job-post-promo-check.svg';
import postJobBannerArrow from '../../assets/job-post-promo-arrow.svg';
import config from '../../config';

import css from './BrowseFreelancersPage.css';
import { FormattedMessage } from 'react-intl';

const VIEWPORT_XSMALL = 360;
const VIEWPORT_SMALL = 720;
const VIEWPORT_MEDIUM = 962;
const VIEWPORT_LARGE = 1301;
const VIEWPORT_XLARGE = 1921;

const decodeHtmlEntities = text => {
  const entities = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&apos;': "'",
    '&#39;': "'",
    '&nbsp;': ' ',
  };
  return text
    .replace(/&(amp|lt|gt|quot|apos|nbsp|#39);/g, match => entities[match] || match)
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(dec))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
};

const fireGtmEvent = author => {
  if (typeof window === 'object') {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'select_item',
      content_type: 'freelancer',
      ecommerce: {
        items: [
          {
            item_id: author.id.uuid,
            item_name: author.attributes.profile.displayName,
            quantity: '1',
            item_list_name: 'search_results',
          },
        ],
      },
    });
  }
};

const fireAddToFavoritesGtmEvent = author => {
  if (typeof window === 'object') {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'add_to_favorites',
      content_type: 'freelancer',
      ecommerce: {
        items: [
          {
            item_id: author.id.uuid,
            item_name: author.attributes.profile.displayName,
            quantity: '1',
            item_list_name: 'search_results',
          },
        ],
      },
    });
    if (typeof window !== 'undefined') {
      trackIntercomEvent('addedUserToFavorites', {
        userId: author.id?.uuid,
        displayName: author.attributes?.profile?.displayName,
      });
    }
  }
};

const getMaxCharsForTwoLines = viewportWidth => {
  if (viewportWidth <= 360) return 80;
  if (viewportWidth <= 720) return 120;
  if (viewportWidth <= 962) return 160;
  if (viewportWidth <= 1301) return 200;
  return 240;
};

const getSafeBioSliceForTwoLines = (text, maxLength) => {
  if (text.length <= maxLength) return text;

  let sliced = text.slice(0, maxLength);

  const lastSpace = sliced.lastIndexOf(' ');
  if (lastSpace > maxLength * 0.8) {
    sliced = sliced.slice(0, lastSpace);
  }

  sliced = sliced.replace(/[.\s,;:!?]+$/, '');

  return sliced;
};

const BrowseFreelancersResults = props => {
  const {
    pageResults,
    currentPageReviews,
    skillOptions,
    toolOptions,
    pagination,
    search,
    routeName,
    favoriteProfileListingsIds,
    handleAddRemoveFavorite,
    otherListings,
    currentUser,
    offPlatformTestimonials,
    offPlatformReviews,
  } = props;

  const [viewportWidth, setViewportWidth] = useState(
    typeof window === 'object' ? window.innerWidth : 0
  );
  useEffect(() => {
    if (typeof window === 'object') {
      setViewportWidth(window.innerWidth);
    }
  }, []);

  if (typeof window === 'object') {
    function updateWidth() {
      setViewportWidth(window.innerWidth);
    }

    // updateWidth();
    window.addEventListener('resize', updateWidth);
  }

  useEffect(() => {
    if (typeof window === 'object') {
      setViewportWidth(window.innerWidth);
    }
  }, []);

  const parentWidth = useMemo(() => {
    let calculatedWidth;
    switch (true) {
      case viewportWidth <= VIEWPORT_XSMALL:
        calculatedWidth = 290;
        break;
      case viewportWidth <= VIEWPORT_SMALL:
        calculatedWidth = viewportWidth - 150;
        break;
      case viewportWidth <= VIEWPORT_MEDIUM:
        calculatedWidth = viewportWidth - 200;
        break;
      case viewportWidth <= VIEWPORT_LARGE:
        calculatedWidth = VIEWPORT_LARGE - 400;
        break;
      case viewportWidth <= VIEWPORT_XLARGE:
        calculatedWidth = (VIEWPORT_LARGE - 220) / 2;
        break;
      default:
        calculatedWidth = 700;
    }
    return calculatedWidth;
  }, [viewportWidth]);

  const skillAndToolOptions = skillOptions.concat(toolOptions);
  const translation = config.locale === 'fi' ? 'fiTranslation' : 'enTranslation';

  const PostJobBanner = () => {
    const redirectPageName = currentUser ? 'NewProjectListingPage' : 'SignupPage';
    return (
      <div className={css.postJobBanner}>
        <div className={css.postJobBannerText}>
          <div className={css.postJobBannerTitle}>
            <FormattedMessage id="BrowseFreelancersPage.postJobBannerTitle" />
          </div>
          <div className={css.postJobBannerDescription}>
            <FormattedMessage id="BrowseFreelancersPage.postJobBannerDescription" />
          </div>
        </div>
        <div className={css.postJobBannerActions}>
          <ul className={css.postJobBannerPills}>
            <li className={css.postJobBannerPill}>
              <img src={postJobBannerCheck} alt="" className={css.postJobBannerPillIcon} />
              <FormattedMessage id="BrowseFreelancersPage.postJobBannerPill1" />
            </li>
            <li className={css.postJobBannerPill}>
              <img src={postJobBannerCheck} alt="" className={css.postJobBannerPillIcon} />
              <FormattedMessage id="BrowseFreelancersPage.postJobBannerPill2" />
            </li>
          </ul>
          <NamedLink name={redirectPageName} className={css.postJobBannerButton}>
            <FormattedMessage id="BrowseFreelancersPage.postJobBannerButton" />
            <img src={postJobBannerArrow} alt="" className={css.postJobBannerArrow} />
          </NamedLink>
        </div>
      </div>
    );
  };

  const ExperienceContainer = props => {
    const { items } = props;
    const containerRef = useRef(null);
    const [visibleItems, setVisibleItems] = useState(items);

    useEffect(() => {
      let totalWidth = 0;
      let itemsToDisplay = [];

      if (containerRef.current && containerRef.current.children) {
        for (let index = 0; index < containerRef.current.children.length; index++) {
          const itemRef = containerRef.current.children[index];

          if (itemRef) {
            const itemWidth = itemRef.getBoundingClientRect().width;
            totalWidth += itemWidth;

            if (totalWidth <= parentWidth - itemsToDisplay.length * 14) {
              itemsToDisplay.push(items[index]);
            } else {
              break;
            }
          }
        }
      }

      setVisibleItems(itemsToDisplay);
    }, [items]);

    return (
      <div className={css.experienceContainer}>
        <div
          ref={containerRef}
          style={{
            maxWidth: parentWidth,
            display: 'flex',
            flexDirection: 'row',
            overflow: 'hidden',
            maxHeight: 70,
            gap: 14,
          }}
        >
          {visibleItems &&
            visibleItems.length > 0 &&
            visibleItems.map((e, ind) => (
              <div key={ind} className={css.skillContainer}>
                {e.isTool && (
                  <ToolLogo logoUrl={toolOptions.find(t => t.key === e.category)?.logoUrl} />
                )}
                <div className={css.heading}>
                  {skillAndToolOptions.find(s => s.key === e.category)?.[translation]}
                </div>
              </div>
            ))}
        </div>
        <div className={css.experienceLeft}>
          {items && items.length > visibleItems.length && `+ ${items.length - visibleItems.length}`}
        </div>
      </div>
    );
  };

  const ResultCard = ({ result, reviews, testimonials, otherListings }) => {
    const publicData = result.attributes.publicData || {};
    const author = ensureUser(result.author) || {};
    const displayName = author.attributes.profile?.displayName || 'Freelancer';
    const bioText = publicData.authorBio || '';

    // Calculate billed tier
    const billedTotal = author?.attributes?.profile?.metadata?.billedTotal;
    let billedTier = null;
    if (billedTotal != null && billedTotal > 0) {
      const tiers = [100000, 50000, 25000, 10000, 5000, 1000];
      const tier = tiers.find(t => billedTotal >= t);
      if (tier && tier >= 1000) {
        billedTier = `€${tier / 1000}K+`;
      } else if (tier) {
        billedTier = `€${tier}+`;
      }
    }

    // Strip all HTML tags, decode entities, and normalize whitespace
    const cleanedBioText = decodeHtmlEntities(
      bioText
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/?p>/gi, ' ')
        .replace(/<\/?[^>]+(>|$)/g, '')
        .replace(/\s+/g, ' ')
        .trim()
    );

    const maxChars = getMaxCharsForTwoLines(viewportWidth);

    const truncatedBio = getSafeBioSliceForTwoLines(cleanedBioText, maxChars);
    const bioToShow =
      truncatedBio.length < cleanedBioText.length ? `${truncatedBio}...` : truncatedBio;

    const authorSkillsKeys = publicData.authorSkillsKeys || [];
    const authorToolsKeys = publicData.authorToolsKeys || [];
    const skillsAndTools = authorSkillsKeys
      .map(key => ({ category: key }))
      .concat(authorToolsKeys.map(key => ({ category: key, isTool: true })));

    const ReviewsStat = () => (
      <div className={css.statItem}>
        <div className={css.statValueRow}>
          <FaStar className={css.starIcon} />
          <span className={css.statValue}>
            {(reviews.reduce((a, b) => a + b?.attributes?.rating, 0) / reviews?.length).toFixed(1)}
          </span>
          <span className={css.statCount}>({reviews.length})</span>
        </div>
        <span className={css.statLabel}>
          <FormattedMessage id="FreelancerProfileUserCard.reviews" />
        </span>
      </div>
    );

    const EarningsStat = () => (
      <div className={css.statItem}>
        <div className={css.statValueRow}>
          <span className={css.statValue}>{billedTier}</span>
        </div>
        <span className={css.statLabel}>
          <FormattedMessage id="FreelancerProfileUserCard.earnings" />
        </span>
      </div>
    );

    const TestimonialsStat = () => (
      <div className={css.statItem}>
        <div className={css.statValueRow}>
          <BiSolidQuoteAltRight className={css.quoteIcon} />
          <span className={css.statValue}>{testimonials.length}</span>
        </div>
        <span className={css.statLabel}>
          <FormattedMessage id="FreelancerProfileUserCard.testimonials" />
        </span>
      </div>
    );

    const favoriteProfileListingsUUIDs = useMemo(() => {
      return favoriteProfileListingsIds ? favoriteProfileListingsIds.map(id => id.uuid) : [];
    }, []);

    const isFavoriteProfile = favoriteProfileListingsUUIDs?.includes(result.id.uuid);

    // Hide favorite icon for freelancers and light-entrepreneurs
    const currentUserRole = currentUser?.attributes?.profile?.publicData?.account_role;
    const shouldShowFavoriteIcon = !currentUserRole || currentUserRole === 'customer';

    return (
      <div onClick={() => fireGtmEvent(author)}>
        <NamedLink name="ProfilePage" params={{ id: author.id.uuid }} className={css.resultCard}>
          <div className={css.resultCardTopRow}>
            <div className={css.userInfoAndStatsDesktop}>
              <div className={css.userInfoSection}>
                <div className={css.avatarContainer}>
                  <AvatarMedium user={author} disableProfileLink={true} />
                </div>
                <div className={css.nameAndTitle}>
                  <div className={css.displayName}>{displayName}.</div>
                  <div className={css.authorWhoAmI}>{publicData.authorWhoAmI}</div>
                </div>
              </div>
              {(reviews && reviews.length) ||
              billedTier ||
              (testimonials && testimonials.length > 0) ? (
                <div className={css.statsSectionDesktop}>
                  {reviews && reviews.length > 0 && <ReviewsStat />}
                  {billedTier && <EarningsStat />}
                  {testimonials && testimonials.length > 0 && <TestimonialsStat />}
                </div>
              ) : null}
              <div className={css.heartSectionMobile}>
                {shouldShowFavoriteIcon && (
                  <>
                    {!isFavoriteProfile ? (
                      <FaRegHeart
                        className={css.addToFavoritesIcon}
                        onMouseDown={event => {
                          event.preventDefault();
                        }}
                        onClick={event => {
                          event.stopPropagation();
                          event.preventDefault();
                          // Prevent any potential scrolling
                          if (event.target) {
                            event.target.blur();
                          }
                          fireAddToFavoritesGtmEvent(author);
                          handleAddRemoveFavorite(isFavoriteProfile, author.id.uuid);
                        }}
                      />
                    ) : (
                      <FaHeart
                        className={css.addToFavoritesIcon}
                        onMouseDown={event => {
                          event.preventDefault();
                        }}
                        onClick={event => {
                          event.stopPropagation();
                          event.preventDefault();
                          // Prevent any potential scrolling
                          if (event.target) {
                            event.target.blur();
                          }
                          handleAddRemoveFavorite(isFavoriteProfile, author.id.uuid);
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            </div>
            {(reviews && reviews.length) ||
            billedTier ||
            (testimonials && testimonials.length > 0) ? (
              <div className={css.statsSectionMobile}>
                {reviews && reviews.length > 0 && <ReviewsStat />}
                {billedTier && <EarningsStat />}
                {testimonials && testimonials.length > 0 && <TestimonialsStat />}
              </div>
            ) : null}
            <div className={css.heartSectionDesktop}>
              {shouldShowFavoriteIcon && (
                <>
                  {!isFavoriteProfile ? (
                    <FaRegHeart
                      className={css.addToFavoritesIcon}
                      onMouseDown={event => {
                        event.preventDefault();
                      }}
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        // Prevent any potential scrolling
                        if (event.target) {
                          event.target.blur();
                        }
                        fireAddToFavoritesGtmEvent(author);
                        handleAddRemoveFavorite(isFavoriteProfile, author.id.uuid);
                      }}
                    />
                  ) : (
                    <FaHeart
                      className={css.addToFavoritesIcon}
                      onMouseDown={event => {
                        event.preventDefault();
                      }}
                      onClick={event => {
                        event.stopPropagation();
                        event.preventDefault();
                        // Prevent any potential scrolling
                        if (event.target) {
                          event.target.blur();
                        }
                        handleAddRemoveFavorite(isFavoriteProfile, author.id.uuid);
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {otherListings && otherListings.length > 0 && (
            <div className={css.resultOtherListingImagesContainer}>
              {otherListings.slice(0, 3).map(listing => {
                const listingType = listing?.attributes?.publicData?.listingType;
                const serviceVariants = ['landscape-crop'];
                const portfolioVariants = ['horizontal'];
                const variants = listingType === 'service' ? serviceVariants : portfolioVariants;
                const image =
                  listing?.images && listing.images.length > 0 ? listing.images[0] : null;
                return (
                  <ResponsiveImage
                    key={listing.id.uuid}
                    className={
                      listingType === 'service'
                        ? css.resultOtherListingImage
                        : css.resultOtherListingImagePortfolio
                    }
                    alt="Other listing image"
                    variants={variants}
                    image={image}
                  />
                );
              })}
            </div>
          )}

          <div className={css.resultBio}>{bioToShow}</div>
          <ExperienceContainer items={skillsAndTools} />
        </NamedLink>
      </div>
    );
  };

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={routeName === undefined ? 'BrowseFreelancersPage' : routeName}
        pageSearchParams={search}
        pagination={pagination}
      />
    ) : null;

  return (
    <div className={css.resultsContainer}>
      {pageResults && pageResults.length > 0 ? (
        <>
          {pageResults.map((result, idx) => {
            const currentAuthorReviews = currentPageReviews?.filter(review => {
              const currentReviewUserId = review?.relationships?.subject?.data?.id?.uuid;
              const authorId = result.author.id.uuid;
              return currentReviewUserId === authorId;
            });
            const currentAuthorOffPlatformTestimonials = offPlatformTestimonials?.filter(
              testimonial => {
                const testimonialUserId = testimonial?.freelancerId;
                const authorId = result.author.id.uuid;
                return testimonialUserId === authorId && testimonial.state === 'published';
              }
            );
            const currentAuthorOffPlatformReviews = offPlatformReviews?.filter(review => {
              const reviewUserId = review?.freelancerId;
              const authorId = result.author.id.uuid;
              return reviewUserId === authorId;
            });
            if (currentAuthorOffPlatformReviews && currentAuthorOffPlatformReviews.length > 0) {
              currentAuthorReviews.push(
                ...currentAuthorOffPlatformReviews.map(r => ({
                  id: { uuid: r.id },
                  attributes: {
                    rating: r.rating || r.reviewRating,
                    content: r.comment,
                  },
                  relationships: {
                    subject: {
                      data: {
                        id: { uuid: r.freelancerId },
                      },
                    },
                  },
                }))
              );
            }
            const currentUserOtherListings = otherListings?.filter(listing => {
              const listingAuthorId = listing?.author?.id?.uuid;
              return listingAuthorId === result.author.id.uuid;
            });

            // Insert PostJobBanner after 4th item if more than 6 results
            if (idx === 3 && pageResults.length > 6) {
              return (
                <React.Fragment key={result.id.uuid}>
                  <ResultCard
                    result={result}
                    reviews={currentAuthorReviews}
                    testimonials={currentAuthorOffPlatformTestimonials}
                    otherListings={currentUserOtherListings}
                  />
                  <PostJobBanner />
                </React.Fragment>
              );
            }

            // Insert PostJobBanner after last item if less than or equal to 6 results
            if (idx === pageResults.length - 1 && pageResults.length <= 6) {
              return (
                <React.Fragment key={result.id.uuid}>
                  <ResultCard
                    result={result}
                    reviews={currentAuthorReviews}
                    testimonials={currentAuthorOffPlatformTestimonials}
                    otherListings={currentUserOtherListings}
                  />
                  <PostJobBanner />
                </React.Fragment>
              );
            }

            return (
              <ResultCard
                key={result.id.uuid}
                result={result}
                reviews={currentAuthorReviews}
                testimonials={currentAuthorOffPlatformTestimonials}
                otherListings={currentUserOtherListings}
              />
            );
          })}
        </>
      ) : (
        <div className={css.noResults}>
          <FormattedMessage id="BrowseFreelancersPage.noResults" />
          <PostJobBanner />
        </div>
      )}
      {paginationLinks}
    </div>
  );
};

export default BrowseFreelancersResults;
